/**
 * "My recent submissions" reciprocity store (V1 contract §8).
 *
 * On every 201 from POST /submissions the client appends {id, ts} to
 * localStorage `trail.my_recent_submissions`. /explore reads the list and
 * ALWAYS renders pins whose id is in it — bypassing the verified-only
 * filter — so a contributor sees their own report on the map immediately
 * ("Your report — pending verification") instead of wondering where it
 * went.
 *
 * Retention: capped at 20 entries, entries older than 24h are pruned on
 * every read/write. `seenOnMap` tracks the one-time "your report is on
 * the map" toast per id.
 *
 * Also owns the `trail.just_submitted_version` sessionStorage handshake:
 * the submit flow stashes the manifest_version returned by the backend on
 * 201; /explore consumes it on mount to fetch that exact data version
 * immediately instead of waiting out the manifest poll/cache window.
 */

const STORAGE_KEY = 'trail.my_recent_submissions';
const JUST_SUBMITTED_KEY = 'trail.just_submitted_version';
const MAX_ENTRIES = 20;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface RecentSubmission {
  /**
   * Raw submission ULID from POST /submissions. NOTE: this is NOT the
   * observation/pin id — the backend writes the map feature as
   * `sub-<ulid>` (submission-create → observation_id: `sub-${id}`).
   * Use observationIdsFor() when matching against map features.
   */
  id: string;
  /** Epoch ms of the 201 response. */
  ts: number;
  /** True once the one-time "on the map" toast has been shown for it. */
  seenOnMap?: boolean;
}

/**
 * Candidate observation-feature ids for a stored submission id. The
 * backend keys user-submission map features as `sub-<submission ulid>`
 * (dregsbane-web-backend submission-create writes
 * `observation_id: 'sub-' + id`; trail-observations serves it as the
 * GeoJSON feature id), while this store keeps the raw ULID from the 201.
 * Matching against BOTH forms keeps reciprocity working whichever
 * convention a given data version uses. Single source of truth for the
 * `sub-` prefix on the client — don't inline it elsewhere.
 */
export function observationIdsFor(submissionId: string): string[] {
  return submissionId.startsWith('sub-')
    ? [submissionId]
    : [submissionId, `sub-${submissionId}`];
}

function prune(entries: RecentSubmission[], now: number): RecentSubmission[] {
  return entries
    .filter((e) => e && typeof e.id === 'string' && typeof e.ts === 'number')
    .filter((e) => now - e.ts <= MAX_AGE_MS)
    .slice(-MAX_ENTRIES);
}

export function readRecentSubmissions(now: number = Date.now()): RecentSubmission[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return prune(parsed as RecentSubmission[], now);
  } catch {
    return [];
  }
}

function write(entries: RecentSubmission[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    /* storage full / privacy mode — reciprocity is best-effort */
  }
}

/** Append a submission id on 201. Dedupes, prunes >24h, caps at 20. */
export function recordRecentSubmission(id: string, now: number = Date.now()): void {
  if (typeof window === 'undefined') return;
  const entries = readRecentSubmissions(now).filter((e) => e.id !== id);
  entries.push({ id, ts: now });
  write(prune(entries, now));
}

/** Mark ids as having had their one-time map toast shown. */
export function markSeenOnMap(ids: string[], now: number = Date.now()): void {
  if (typeof window === 'undefined' || ids.length === 0) return;
  const idSet = new Set(ids);
  const entries = readRecentSubmissions(now).map((e) =>
    idSet.has(e.id) ? { ...e, seenOnMap: true } : e,
  );
  write(entries);
}

/**
 * Stash the manifest_version returned by POST /submissions so /explore
 * can fetch it immediately on next mount (V1 contract §8).
 */
export function recordJustSubmittedVersion(version: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(JUST_SUBMITTED_KEY, version);
  } catch {
    /* best-effort */
  }
}

/** Read + clear the just-submitted manifest version (consume-once). */
export function consumeJustSubmittedVersion(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.sessionStorage.getItem(JUST_SUBMITTED_KEY);
    if (v !== null) window.sessionStorage.removeItem(JUST_SUBMITTED_KEY);
    return v;
  } catch {
    return null;
  }
}
